// src/BackButtonHandler.tsx
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, matchPath } from 'react-router-dom';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

export default function BackButtonHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const lastBackPress = useRef<number>(0);

  useEffect(() => {
    let listenerHandle: any = null;

    const setupListener = async () => {
      // Check if current path matches cruise details page pattern
      const isCruiseDetailsPage = matchPath(
        { path: '/cruise/:id' },
        location.pathname
      );

      // Only set up Capacitor listener on native platforms
      if (Capacitor.isNativePlatform()) {
        listenerHandle = await App.addListener('backButton', ({ canGoBack }) => {
          // Special handling for cruise details pages - always go back to dashboard
          if (isCruiseDetailsPage) {
            navigate('/dashboard', { replace: true });
            return;
          }

          // If there is router/webview history, go back
          if (canGoBack || window.history.length > 1) {
            navigate(-1);
            return;
          }

          // We are at a "root" route (no history). Optional: double-press to exit.
          const now = Date.now();
          if (now - lastBackPress.current < 1500) {
            App.exitApp(); // or App.minimizeApp() if you prefer
          } else {
            lastBackPress.current = now;
            // You can show a toast here like "Press back again to exit"
            // e.g. using react-hot-toast:
            // toast('Press back again to exit', { duration: 1500 });
          }
        });
      } else {
        // Web platform: handle browser back button via popstate event
        const handlePopState = (event: PopStateEvent) => {
          // Special handling for cruise details pages - always go back to dashboard
          if (isCruiseDetailsPage) {
            event.preventDefault();
            navigate('/dashboard', { replace: true });
            return;
          }
        };

        window.addEventListener('popstate', handlePopState);
        
        // Store cleanup function for web
        listenerHandle = {
          remove: () => {
            window.removeEventListener('popstate', handlePopState);
          }
        };
      }
    };

    setupListener();

    return () => {
      if (listenerHandle && listenerHandle.remove) {
        listenerHandle.remove();
      }
    };
  }, [navigate, location.pathname]);

  return null;
}